/**
 * Directions の生経路を、地図に描ける形へ整える後処理。
 *
 * **この合成 (どの工程をどの順に呼ぶか) が唯一の定義**。v1 / v2 / シード焼き込み
 * スクリプトが同じ関数を呼ぶこと。
 *
 * 以前は autodraft.ts と scripts/bake-seeds.mjs が同じ合成を別々に書いており、
 * bake 側から境界トリムの工程が丸ごと抜け落ちていた。結果、Directions が避難先の
 * 内部を辿った生の折れ線がそのまま矢印になり、避難場所付近で逆走していた
 * (実測: おりづるタワーで 99.2° の折れ)。部品 (trim/simplify/extend) を共有する
 * だけでは同じ事故が再発する — 抜け落ちたのは部品ではなく合成だったため。
 *
 * このファイルは意図的に Vite 非依存にしてある。autodraft.ts は Mapbox クライアント
 * 経由で `import.meta.env` に触れるため、node で動く bake スクリプトから import
 * できない。合成をここに置くことでスクリプト側も同じ実装を使える。
 */
import {
  clipStartAtRingExit,
  densifyRouteCoords,
  extendRouteIntoPolygon,
  nearestPointOnPolygonBoundary,
  pointInsideRing,
  removeMicroSpurs,
  removeShallowBends,
  simplifyRouteCoords,
  trimRouteAtPolygonBoundary,
  trimRouteStartInsidePolygon,
} from './polygon-utils';
import { snapRouteToRoads } from './route-snap';
import { trimRouteAtHoles } from './polygon-holes';
import { haversineMeters } from './distance';
import {
  DESTINATION_PENETRATION_M,
  ROUTE_SIMPLIFY_TOLERANCE_M,
  SNAPPED_SIMPLIFY_TOLERANCE_M,
} from './route-tuning';

import type { LngLat } from '@/types/property';

/** ring の配列を持つポリゴン (DestinationV2['polygon'] / PropertyV2['footprint'] 互換)。 */
export interface PolygonRings {
  coordinates: Array<Array<[number, number]>>;
}

/**
 * 複数エリアのうち「基準点に最も近いエリア(外周ring)」を返す。
 * 経路の終点・切り詰めはこの ring を基準にする (駒場東大一帯=3エリア等)。
 */
export function nearestRing(
  from: LngLat,
  polygon: PolygonRings | null | undefined,
): Array<[number, number]> | null {
  const rings = polygon?.coordinates ?? [];
  let best: Array<[number, number]> | null = null;
  let bestDist = Infinity;
  for (const ring of rings) {
    if (ring.length < 3) continue;
    const p = nearestPointOnPolygonBoundary(ring, from);
    if (!p) continue;
    const d = haversineMeters(from, p);
    if (d < bestDist) {
      bestDist = d;
      best = ring;
    }
  }
  return best;
}

/**
 * 複数エリアの避難先で、経路の行き先とするエリア。
 *
 * 代表点 (destCoords) を内包するエリアがあればそれ、無ければ物件に最も近いエリア。
 * 作成時は代表点を最寄りエリアの重心に置く (finalizeDestination) ので通常は最寄りと同じ。
 * 奥のエリアへ行かせたいときは代表点をそのエリアへ移せば、経路の取り直し・候補でも保たれる
 * (実測: 立教大学 = 西池袋公園の街区 + キャンパス。最寄り固定だと「経路変更」で公園側へ戻った)。
 */
export function destinationRing(
  property: LngLat,
  destCoords: LngLat,
  polygon: PolygonRings | null | undefined,
): Array<[number, number]> | null {
  const rings = polygon?.coordinates ?? [];
  if (rings.length > 1) {
    const chosen = rings.find(
      (r) => r.length >= 3 && pointInsideRing(destCoords, r),
    );
    if (chosen) return chosen;
  }
  return nearestRing(property, polygon);
}

/**
 * 経由点のうち「避難先の入口」とみなすものを分ける。
 *
 * 最後の経由点が行き先エリアの**内側**にあれば、それが入口 = 経路の終点。経路はそこで
 * 終え、自動で選んだ進入点へは向かわない。公開地図に無い入口をユーザーが指定する手段
 * (実測: 第五中学校の北西口 = 五中つつじ通りの突き当たり。Mapbox のタイルにこの道が無く、
 * 経由点を置いても入口を通り過ぎて縁沿いに南西の進入点まで歩いていた)。
 *
 * 外側の経由点は「ここを通る」のまま。縁の数m外に置いた横断・陸橋の経由点
 * (実測: 川口 3m・門前仲町 5m) を入口に変えないよう、判定は内側かどうかだけで行う。
 */
export function splitEntranceWaypoint(
  waypoints: LngLat[],
  ring: Array<[number, number]> | null,
): { entrance: LngLat | null; vias: LngLat[] } {
  const last = waypoints.at(-1);
  if (!last || !ring || !pointInsideRing(last, ring)) {
    return { entrance: null, vias: waypoints };
  }
  return { entrance: last, vias: waypoints.slice(0, -1) };
}

/** 入口の進入点を切り替える最小の差 (m)。数mの差で経路を引き直さない。 */
const ENTRANCE_GATE_MARGIN_M = 10;

/**
 * 入口の経由点 (splitEntranceWaypoint) へ、入口に一番近い道から入るための進入点。
 *
 * Directions に範囲の内側の点を渡すと、範囲の中の通路を歩いて届く経路を返すことがあり、
 * 範囲に入る位置が入口から遠くなる (実測: 大森Ⅲ — 北西の通路から入り、公園内を
 * 南へ歩いていた。入口の 24m 先に西の道との進入点がある)。経路が範囲に入る位置より
 * 入口に近い進入点 (道 × 境界) があれば、それを返す。呼び出し側はそこまで経路を引き、
 * 内側は入口まで直に結ぶ。経路が既に入口の近くで入る (地図に無い道の突き当たりに
 * 置いた入口 = 第五中学校 など) ときや、進入点が無いときは null。
 */
export function entranceGate(
  route: Array<[number, number]>,
  ring: Array<[number, number]>,
  entrance: LngLat,
  entries: LngLat[],
): LngLat | null {
  if (entries.length === 0) return null;
  const gate = entries.reduce((a, b) =>
    haversineMeters(b, entrance) < haversineMeters(a, entrance) ? b : a,
  );
  const trimmed = trimRouteAtPolygonBoundary(route, ring);
  const cross = trimmed.at(-1);
  if (!cross) return gate;
  const crossD = haversineMeters({ lng: cross[0], lat: cross[1] }, entrance);
  return haversineMeters(gate, entrance) + ENTRANCE_GATE_MARGIN_M < crossD ? gate : null;
}

/**
 * 生経路を「境界で切詰 → 直線化 → 内部へ少し延長」する。
 *
 * - 切詰: 経路が避難先の内部へ貫通した分を境界で落とす。これを省くと避難先の中を
 *   うろついた線がそのまま矢印になる。
 * - 直線化: 実経路の細かいガタガタを間引いて「経路に沿った直線」に近づける。
 * - 延長: 矢印を入り口から内部へ少し入り込ませる (通常仕様)。
 *
 * destRing が無い (形状不明の避難先) 場合は直線化のみ。
 * startRing (物件 footprint) を渡すと、経路が物件の塗りの内側から始まる分も境界で落とす
 * (始点が物件座標にフォールバックした経路が塗りを貫通するのを防ぐ)。
 *
 * opts.trimDest=false で終点側の境界トリムをスキップする。**特定の進入点へ向かう候補**用 —
 * 境界沿いの街路がポリゴンを掠めると「最初に触れた点」で切られ、遠い入口への経路が
 * すべて同一の切り詰め結果に潰れて dedupe で消える (実測: 新川ツインビル地区の南側入口)。
 * 進入点候補は Directions の終点が既に境界上の入口なので、トリム無しでも内部を
 * うろつく線にはならない (内部への延長はどちらのモードでも適用)。
 *
 * opts.destHoles を渡すと、最後に除外区域 (穴) へ入る手前で経路を終える (trimRouteAtHoles)。
 *
 * opts.roadLines を渡すと、簡略化の**前に**経路を車道中心線へスナップする
 * (route-snap.ts)。Mapbox 徒歩グラフの分離歩道・横断歩道由来の微細ジオメトリを
 * スキーマティックな中心線経路に集約するため。trim の後 = 切り詰めで残った区間だけを
 * スナップし、simplify の前 = スナップで生じた共線頂点を後段で間引く。
 */
export function postprocessRoute(
  coords: Array<[number, number]>,
  destRing: Array<[number, number]> | null,
  startRing?: Array<[number, number]> | null,
  opts: {
    trimDest?: boolean;
    roadLines?: Array<Array<[number, number]>> | null;
    /** 行き先エリアの中の除外区域 (holesWithin)。経路はそこへ入る手前で終える。 */
    destHoles?: Array<Array<[number, number]>>;
    /** 入口の経由点 (splitEntranceWaypoint)。内側は中心へ延ばさず、入口まで結ぶ。 */
    entrance?: LngLat | null;
  } = {},
): Array<[number, number]> {
  const trimDest = opts.trimDest !== false;
  const roadLines = opts.roadLines ?? [];
  const snapped = roadLines.length > 0;
  let out =
    destRing && trimDest ? trimRouteAtPolygonBoundary(coords, destRing) : coords;
  // 物件 footprint の内側から始まる分を境界で落とす (簡略化の前に = 貫通部を除いてから直線化)。
  if (startRing) out = trimRouteStartInsidePolygon(out, startRing);
  if (snapped) {
    // 回避リング: trim が確立した「経路はポリゴン外」をスナップが破らないように、
    // 射影が避難先/物件ポリゴンの内側へ落ちる頂点はスナップしない。
    const avoidRings = [destRing, startRing].filter(
      (r): r is Array<[number, number]> => !!r,
    );
    // 密度化してからスナップ: スナップは頂点しか動かせない。長い弦 (実測: 本川小
    // A7 の 154m) は中間頂点が無いと車道へ寄らない。間隔はスナップの捕捉半径と
    // 同程度の 25m — 挿した点はスナップ後に共線となり後段の simplify が間引く。
    out = snapRouteToRoads(densifyRouteCoords(out, 25), roadLines, {
      avoidRings,
    });
  }
  // 平滑化の誤差予算: 中心線スナップ済みなら小さく絞る。12m は生の歩道ノイズ向けの
  // 許容で、スナップ後に使うと狭い街区 (30-50m) の角や入口進入部を最大 12m 斜めに
  // 切る弦が残り「道でない場所を横切る」矢印になる (実測: 阪本小学校の候補群)。
  const epsM = snapped
    ? SNAPPED_SIMPLIFY_TOLERANCE_M
    : ROUTE_SIMPLIFY_TOLERANCE_M;
  out = simplifyRouteCoords(out, epsM);
  if (snapped) {
    // スナップのガード (曖昧・スパイク・角度) が意図的に残した生ジオメトリの断片は
    // 「短い脚の鋭角ジャグ」として露出する (許容 5m の間引きでは消えない)。専用除去。
    // **simplify の後に**実行する: 角補完で挿入した交差点の角は、隣に横断歩道由来の
    // 冗長頂点が至近 (~10m) にあり脚が短く見える。simplify が先に共線頂点を間引けば
    // 角の脚は本来の長さに戻り、脚長ゲートが実在の角を守れる (実測: 阪本小 A1/A4/A10
    // で角が削られ交差点を斜めに省略していた)。
    out = removeMicroSpurs(out);
  } else {
    // 実質一本道なのに W 字に見えるジグザグ (迂回量の小さい折れ) を畳む。
    // douglas-peucker は振れ幅基準のため取り切れない (実測: 阪本小学校 134m 経路)。
    // **スナップ済み経路には適用しない** — アンカー直角化が作る「垂直に出て道なりへ」
    // の ±90° は符号交代・小迂回・小逸脱で、この除去条件にちょうど一致してしまい、
    // 垂線の足や交差点の角を刈って矩形経路を斜め線に戻す (実測: 阪本小 A1 の
    // [88°,92°,90°] が [78°] の斜行 + 楔 [116°,116°] に化けた)。スナップ経路の
    // ノイズ除去は snap 内のガード + removeMicroSpurs が担う。
    out = removeShallowBends(out);
  }
  // スナップ/直角化後の始点リードが物件塗りの張り出しを横切っていたら、塗りを
  // 出る交点から始める (実測: 横浜ランドマークタワー — 第1脚が塗りを20m横断)。
  if (startRing) out = clipStartAtRingExit(out, startRing);
  if (destRing && opts.entrance) {
    out = [...out, [opts.entrance.lng, opts.entrance.lat]];
  } else if (destRing) {
    out = extendRouteIntoPolygon(out, destRing, DESTINATION_PENETRATION_M);
  }
  // 延長や範囲内を進む候補が除外区域 (水面など) に入ったら、その手前で終える。
  return trimRouteAtHoles(out, opts.destHoles);
}
